<template>
  <div class="page-view">

    <BasePageHeader :title="$t('areas.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isManagerOrAbove" to="/bulk-edit/areas" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button v-if="isManagerOrAbove" class="btn primary sm" @click="openCreate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Filters -->
    <div class="px-4 pt-3 pb-2 flex gap-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('areas.searchPlaceholder')" />
      <BaseSelect v-model="warehouseFilter" density="compact" :aria-label="$t('areas.allWarehouses')">
        <option value="">{{ $t('areas.allWarehouses') }}</option>
        <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
      </BaseSelect>
    </div>

    <AreaForm
      v-if="formOpen && !editing"
      v-model="form"
      :locations="locations"
      :warehouses="warehouses"
      :saving="saving"
      :title="$t('common.create')"
      class="mx-4 my-2"
      @save="save"
      @cancel="closeForm"
    />

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState v-else-if="!filtered.length" :message="query ? $t('areas.noMatch') : $t('areas.noAreas')" />

    <div v-else class="list-stack">
      <template v-for="area in filtered" :key="area.id">
        <div v-if="editing?.id === area.id" ref="inlineFormEl">
          <AreaForm
            v-model="form"
            :locations="locations"
            :warehouses="warehouses"
            :saving="saving"
            :title="$t('common.update')"
            @save="save"
            @cancel="closeForm"
          />
        </div>
        <div v-else class="data-card">
          <div class="data-card__main">
            <div class="data-card__name">{{ area.name }}</div>
            <div class="data-card__meta">
              <span v-if="warehouseName(area.warehouse)" class="badge badge-accent">{{ warehouseName(area.warehouse) }}</span>
            </div>
          </div>
          <div class="data-card__actions">
            <BaseIconButton v-if="isManagerOrAbove" :title="$t('common.edit')" @click="openEdit(area)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
            </BaseIconButton>
            <BaseIconButton v-if="isManagerOrAbove" variant="danger" :title="$t('common.delete')" @click="remove(area)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
            </BaseIconButton>
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useToast } from '@/composables/useToast'
import { useStore } from '@/stores/store'
import { usePermissions } from '@/composables/usePermissions.js'
import { getAreas, createArea, updateArea, deleteArea, getWarehouses, getLocations } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import SearchField from '@/components/search/SearchField.vue'
import AreaForm from '@/components/AreaForm.vue'
import { useI18n } from 'vue-i18n'

const toast = useToast()
const store = useStore()
const { isManagerOrAbove } = usePermissions()
const { t } = useI18n()
const query = ref('')
const warehouseFilter = ref('')
const areas = ref([])
const warehouses = ref([])
const locations = ref([])
const loading = ref(false)
const loadError = ref(null)
const formOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const form = ref({ name: '', location: '', warehouse: '' })
const inlineFormEl = ref(null)

function warehouseName(id) {
  if (!id) return null
  const w = warehouses.value.find(w => w.id === id)
  return w?.name ?? `#${id}`
}

const filtered = computed(() => {
  let list = areas.value
  if (warehouseFilter.value) list = list.filter(a => a.warehouse === Number(warehouseFilter.value))
  const q = query.value.trim().toLowerCase()
  if (!q) return list
  return list.filter(a => a.name?.toLowerCase().includes(q))
})

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const [areaRes, whRes, locRes] = await Promise.all([getAreas(), getWarehouses(), getLocations()])
    areas.value = areaRes.results ?? areaRes ?? []
    warehouses.value = whRes.results ?? whRes ?? []
    locations.value = locRes.results ?? locRes ?? []
  } catch {
    loadError.value = t('areas.loadFailed')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = null
  form.value = {
    name: '',
    location: store.currentLocation?.id ?? '',
    warehouse: store.currentWarehouse?.id ?? '',
  }
  formOpen.value = true
}

function openEdit(area) {
  editing.value = area
  const wh = warehouses.value.find(w => w.id === area.warehouse)
  form.value = {
    name: area.name,
    location: wh?.location?.id ?? wh?.location ?? '',
    warehouse: area.warehouse ?? '',
  }
  formOpen.value = true
  nextTick(() => {
    const el = Array.isArray(inlineFormEl.value) ? inlineFormEl.value[0] : inlineFormEl.value
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

function closeForm() {
  formOpen.value = false
  editing.value = null
}

async function save() {
  saving.value = true
  try {
    const payload = { name: form.value.name, warehouse: Number(form.value.warehouse) }
    if (editing.value) {
      const updated = await updateArea(editing.value.id, payload)
      const idx = areas.value.findIndex(a => a.id === editing.value.id)
      if (idx !== -1) areas.value[idx] = updated
      toast.success(t('areas.updated'))
    } else {
      const created = await createArea(payload)
      areas.value.unshift(created)
      toast.success(t('areas.created'))
    }
    closeForm()
  } catch {
    toast.error(t('areas.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function remove(area) {
  if (!confirm(t('areas.deleteConfirm', { name: area.name }))) return
  try {
    await deleteArea(area.id)
    areas.value = areas.value.filter(a => a.id !== area.id)
    toast.success(t('areas.deleted'))
  } catch {
    toast.error(t('areas.deleteFailed'))
  }
}

onMounted(load)
</script>

